<script lang="ts">
	import { push } from 'svelte-spa-router';
	import {
		Modal,
		Alert,
		AlertV2,
		Container,
		Input,
		InputV3,
		Button,
		ButtonV2,
		Text,
		LoginFormContainer,
	} from '@Shelly/shelly-design-system';
	import { profiles } from 'src/stores/features/profiles';
	import { clearToken, standardLogin } from 'src/services/http';
	import { user_data, user_settings } from 'src/stores/account/user';
	import { initializing_language } from 'src/stores/session/preferences';
	import { format } from 'src/services/i18n';
	import {
		clearDataOnLogin,
		handleLoginErrors,
		handleSingleProfileLogout,
	} from 'src/helpers/account_helpers';

	import sha1 from 'crypto-js/sha1';

	import { handleUserProfile } from 'src/helpers/login_helpers';
	import { MAX_LOGGED_IN_PROFILES } from 'src/constants';
	import type { UserData } from 'src/types/UserData';
	import { mobile } from 'src/services/mobile-rework';
	import { capFirstChar, fillMissingUnitNames } from 'src/helpers/utilities';
	import { testId } from 'src/helpers/test_helpers';

	import SignUpForm from './SignUpForm.svelte';
	import SignUpFormV2 from './SignUpFormV2.svelte';
	import ForgottenPasswordForm from './ForgottenPasswordForm.svelte';
	import ForgottenPasswordFormV2 from './ForgottenPasswordFormV2.svelte';
	import { onMount } from 'svelte';
	import { socket_disconnect } from 'src/services/socket';
	import { LANG_DICT } from 'src/constants';
	import { changeLanguage } from './profiles_helpers';
	import { stopAndClearNotifications } from 'src/helpers/notifications/device_notifications_helper';
	import LangDropdown from '../HelperComponents/LangDropdown.svelte';
	import LangDropdownV2 from '../HelperComponents/LangDropdownV2.svelte';
	import { get } from 'svelte/store';
	import { nativeLogin } from 'src/services/auth_v2';
	import { normalizeEmail } from 'src/helpers/email_helpers';
	import { validate_email } from 'src/helpers/validators';
	import type { AvailableSubUnits, AvailableUnits } from 'src/types/responses/usersAuthResponse';
	import UnitSelectionModal from 'src/components/UnitSelectionModal.svelte';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';

	export let showModal = false;
	export let title = $format('modal_title_add_another_profile', {
		default: 'Add Another Profile',
	});
	export let logoutFromCurrentWihtLogin = false;

	let isFormValid = true;
	let userEmail = '';
	let userEmailError = '';
	let userPassword = '';
	let userPasswordError = '';
	let isLoading = false;
	let showAlert = false;
	let alertProps = {
		heading: '',
		message: '',
	};
	let showDropdownMenu = false;
	let showUnitModal = false;
	let availableUnits: AvailableUnits = {};
	let availableSubUnits: AvailableSubUnits = {};
	let storedHashPass = '';
	let discoveryToken: string | undefined;
	let discoveryApiUrl: string | undefined;
	let outgoingUserData: UserData | undefined;

	$: lang = $user_settings.lang
		? formatLang($user_settings.lang)
		: $initializing_language
			? formatLang($initializing_language)
			: 'EU';

	const dropdownItemsData = Object.entries(LANG_DICT).map((langObj) => {
		const subtitle =
			langObj[0].length > 2 ? langObj[0].slice(0, 2).toUpperCase() : langObj[0].toUpperCase();
		return {
			title: langObj[1],
			subtitle,
			language: langObj[0],
		};
	});

	const clearErrors = () => {
		userEmailError = '';
		userPasswordError = '';
		isFormValid = true;
		showAlert = false;
	};

	const clearData = () => {
		userEmail = '';
		userPassword = '';
	};

	const onCloseModal = () => {
		if (isLoading) return;
		showModal = false;
		logoutFromCurrentWihtLogin = false;
		showDropdownMenu = false;
		signupView = false;
		forgottenPasswordView = false;
		clearData();
		clearErrors();
		outgoingUserData = undefined;
	};

	const checkIsFormValid = () => {
		if (userEmail === '') {
			userEmailError = $format('label_enter_email', {
				default: 'Please enter an email!',
			});
		} else {
			const isValidEmail = validate_email(userEmail);
			userEmailError = !isValidEmail
				? $format('input_error_valid_email', {
						default: 'Please enter a valid email!',
					})
				: '';
		}

		if (userPassword === '') {
			userPasswordError = $format('label_enter_password', {
				default: 'Please enter a password!',
			});
		}

		return !userEmailError && !userPasswordError;
	};

	function formatLang(lang?: string): string {
		if (!lang) return 'EU';

		return lang.length > 2 ? lang.slice(0, 2).toUpperCase() : lang.toUpperCase();
	}

	const completeLogin = async (res, hashPass: string, email: string, outgoingUserData?: UserData) => {
		const currentUserData = get(user_data);
		const isInstaller = !!currentUserData?.installer;
		const installerEmail = currentUserData?.email;

		socket_disconnect();
		stopAndClearNotifications();

		// `authLogin` already replaced `user_data` with the new session, so the
		// outgoing profile must come from the snapshot taken before login (FE-2492).
		if (logoutFromCurrentWihtLogin && outgoingUserData) {
			await handleSingleProfileLogout(outgoingUserData);
			logoutFromCurrentWihtLogin = false;
		}

		clearDataOnLogin();

		nativeLogin(email, hashPass, res.data.user_api_url, res.data.token, res.data.lang);

		if (isInstaller) profiles.remove(installerEmail);
		await handleUserProfile(hashPass);
		isLoading = false;
		onCloseModal();
	};

	const onLogin = async () => {
		userEmail = normalizeEmail(userEmail);

		isFormValid = checkIsFormValid();
		if (!isFormValid) {
			return;
		}
		isLoading = true;

		outgoingUserData = { ...get(user_data) } as UserData;
		const hashPass = sha1(userPassword).toString();
		storedHashPass = hashPass;
		let res = await standardLogin(userEmail, hashPass, undefined, undefined);

		console.log('res', res?.isok && 'multipleUnits' in res);
		if (res?.isok && 'multipleUnits' in res) {
			availableUnits = fillMissingUnitNames(res.availableUnits);
			availableSubUnits = res.availableSubUnits ?? {};
			discoveryToken = res.data?.token;
			discoveryApiUrl = res.data?.user_api_url;
			const unitIds = Object.keys(availableUnits);
			const subUnitIds = Object.keys(availableSubUnits);

			const isSingleOwned = unitIds.length === 1 && subUnitIds.length === 0;
			const singleSubValue = subUnitIds.length === 1 ? availableSubUnits[subUnitIds[0]] : null;
			const isSingleShared =
				unitIds.length === 0 &&
				subUnitIds.length === 1 &&
				singleSubValue &&
				typeof singleSubValue !== 'string';

			if (isSingleOwned || isSingleShared) {
				const hasTokens = res.accessToken && res.refreshToken;
				if (!hasTokens) {
					res = await standardLogin(
						userEmail,
						hashPass,
						isSingleOwned ? unitIds[0] : subUnitIds[0],
						isSingleShared ? String(singleSubValue.sub) : undefined,
					);
				}
				if (res?.isok) {
					await completeLogin(res, hashPass, userEmail, outgoingUserData);
				} else {
					isLoading = false;
					alertProps = handleLoginErrors(res);
					showAlert = true;
				}
			} else {
				showUnitModal = true;
				isLoading = false;
			}
			return;
		}

		if (res?.isok) {
			await completeLogin(res, hashPass, userEmail, outgoingUserData);
		} else {
			isLoading = false;
			alertProps = handleLoginErrors(res);
			showAlert = true;
		}
	};

	const onUnitSelected = async (event: CustomEvent<{ unitId: string; subId?: number }>) => {
		showUnitModal = false;
		isLoading = true;

		const { unitId, subId } = event.detail;
		const res = await standardLogin(
			userEmail,
			storedHashPass,
			unitId,
			subId != null ? String(subId) : undefined,
		);

		if (res?.isok) {
			await completeLogin(res, storedHashPass, userEmail, outgoingUserData);
		} else {
			isLoading = false;
			alertProps = handleLoginErrors(res);
			showAlert = true;
		}
	};

	let forgottenPasswordView = false;
	let signupView = false;

	const toggleForgottenPasswordView = () => {
		if (!isLoading) {
			showDropdownMenu = false;
			forgottenPasswordView = !forgottenPasswordView;
		}
	};

	const toggleSignupView = () => {
		if (!isLoading) {
			showDropdownMenu = false;
			signupView = !signupView;
		}
	};

	const handleRegistered = (e: CustomEvent<{ email: string; password: string }>) => {
		userEmail = e.detail.email;
		userPassword = e.detail.password;
		toggleSignupView();
		onLogin();
	};

	const closeModal = (e: CustomEvent) => {
		if (showModal) {
			e.preventDefault();
			onCloseModal();
		}
	};

	const changeLanguageFunc = (dropdownItem) => {
		showDropdownMenu = false;
		changeLanguage(dropdownItem);
	};

	onMount(() => {
		if (mobile.isMobile) {
			mobile.addEventListener('back_button', closeModal);
			return () => mobile.removeEventListener('back_button', closeModal);
		}
	});
</script>

<Modal on:close={onCloseModal} {title} {showModal}>
	<Container slot="content" padding="0">
		<LoginFormContainer>
			{#if !forgottenPasswordView && !signupView}
				<div class="d-flex justify-content-between mb-l">
					<Text element="h1" class={$showNewLayout ? 'text-left pl-s' : 'text-left'}>
						{$format('login')}
					</Text>
					<svelte:component
						this={$showNewLayout ? LangDropdownV2 : LangDropdown}
						bind:showDropdown={showDropdownMenu}
						dropdownItems={dropdownItemsData}
						{lang}
						selectedLang={$user_settings.lang}
						on:change-lang={(e) => {
							changeLanguageFunc(e.detail);
						}}
					/>
				</div>

				{#if showAlert}
					<svelte:component
						this={$showNewLayout ? AlertV2 : Alert}
						class="mb-m"
						type="danger"
						heading={alertProps.heading}
						message={alertProps.message}
					/>
				{/if}

				<svelte:component
					this={$showNewLayout ? InputV3 : Input}
					bind:value={userEmail}
					class="mb-m"
					size="l"
					type="text"
					inputmode={'email'}
					name="email"
					id="login-email-input"
					data-testid={testId('login-modal-email-input')}
					aria-label={$format('placeholder_email', { default: 'e-mail' })}
					label={capFirstChar($format('placeholder_email', { default: 'e-mail' }))}
					icon={$showNewLayout ? 'fa-regular fa-envelope' : undefined}
					disabled={isLoading}
					error={userEmailError}
					on:enter={onLogin}
					on:clear={clearErrors}
					on:input={clearErrors}
				/>

				<svelte:component
					this={$showNewLayout ? InputV3 : Input}
					bind:value={userPassword}
					class="mb-s"
					size="l"
					type="password"
					id="login-password-input"
					data-testid={testId('login-modal-password-input')}
					aria-label={$format('placeholder_password', { default: 'password' })}
					label={capFirstChar($format('placeholder_password', { default: 'password' }))}
					icon={$showNewLayout ? 'fa-regular fa-lock-keyhole' : undefined}
					disabled={isLoading}
					error={userPasswordError}
					a11yProps={{
						iconButtonAriaLabel: $format('label_show_password', {
							default: 'Show password',
						}),
					}}
					on:enter={onLogin}
					on:clear={clearErrors}
					on:input={clearErrors}
				/>

				<Text
					element="div"
					title={$format('forgotten_password')}
					class={`d-flex link-styles ${$showNewLayout ? 'hover-opacity justify-content-end' : 'justify-content-start'} mb-m text-small${isLoading ? ' disabled' : ''}`}
					on:click={toggleForgottenPasswordView}
				>
					{$showNewLayout
						? $format('forgot_password', { default: 'Forgot password?' })
						: $format('forgotten_password')}
				</Text>

				<svelte:component
					this={$showNewLayout ? ButtonV2 : Button}
					width="full"
					class="mb-m"
					data-testid={testId('login-modal-submit-button')}
					responsiveSize={{ sm: 'm' }}
					loading={isLoading}
					disabled={isLoading ||
						($profiles.length >= MAX_LOGGED_IN_PROFILES && !logoutFromCurrentWihtLogin)}
					on:click={onLogin}
				>
					{$format('login', { default: 'Log in' })}
				</svelte:component>

				<Text class="text-small">
					{$format('text_by_logging', {
						default: 'By logging in, you agree to',
					})}
					<span
						role="presentation"
						class={`text-small link-styles${$showNewLayout ? ' hover-opacity' : ''}`}
						on:click={() => push(`/terms-and-conditions`)}
						>{$format('shelly_terms_and_conditions', {
							default: 'Shelly’s Terms and conditions',
						})}</span
					>{', '}
					<span
						role="presentation"
						class={`text-small link-styles${$showNewLayout ? ' hover-opacity' : ''}`}
						on:click={() => push(`/privacy-policy`)}
						>{$format('privacy', { default: 'Privacy policy' })}</span
					>
					{$format('and', { default: 'and' })}
					<span
						role="presentation"
						class={`text-small link-styles${$showNewLayout ? ' hover-opacity' : ''}`}
						on:click={() => push(`/data-act`)}
						>{$format('data_act', { default: 'Data Act' })}</span
					>.
				</Text>

				{#if $showNewLayout}
					<Text class="mt-s text-small">
						{$format('change_log_intro', {
							default: 'Keep up with what’s new and improved in the',
						})}
						<span
							role="presentation"
							class="text-small link-styles hover-opacity"
							on:click={() => push(`/changelog`)}
						>
							{capFirstChar(
								$format('shelly_change_log', {
									default: 'Shelly Changelog',
								}),
							)}</span
						>.
					</Text>
				{/if}

				<hr class={$showNewLayout ? 'mt-xl mb-xl' : 'mt-l mb-l'} />

				<svelte:component
					this={$showNewLayout ? ButtonV2 : Button}
					width="full"
					variant="secondary"
					on:click={toggleSignupView}
					disabled={isLoading ||
						($profiles.length >= MAX_LOGGED_IN_PROFILES && !logoutFromCurrentWihtLogin)}
				>
					{$format('create_account', { default: 'Create account' })}
				</svelte:component>
			{:else if signupView}
				<svelte:component
					this={$showNewLayout ? SignUpFormV2 : SignUpForm}
					idPrefix="signup-modal"
					testIdPrefix="signUp-modal"
					showLangDropdown={true}
					bind:showDropdownMenu
					{lang}
					{dropdownItemsData}
					on:dropdown-item-click={(e) => {
						changeLanguageFunc(e.detail);
					}}
					on:close={toggleSignupView}
					on:registered={handleRegistered}
				/>
			{:else if forgottenPasswordView}
				<svelte:component
					this={$showNewLayout ? ForgottenPasswordFormV2 : ForgottenPasswordForm}
					on:close={toggleForgottenPasswordView}
				/>
			{/if}
		</LoginFormContainer>
	</Container>
</Modal>

{#if showUnitModal}
	<UnitSelectionModal
		bind:showModal={showUnitModal}
		units={availableUnits}
		subUnits={availableSubUnits}
		accountTitle={userEmail}
		accountEmail={''}
		{isLoading}
		authToken={discoveryToken}
		userApiUrl={discoveryApiUrl}
		on:select={onUnitSelected}
	/>
{/if}
